import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { SkuDmsLink, SkuDmsLinkKind } from '../types';
import { wmsSkuKeys } from './useWmsSkus';

const dmsLinkKeys = {
  list: (skuId: string) => ['wms-sku-dms-links', skuId] as const,
};

export function useSkuDmsLinks(skuId: string | undefined) {
  return useQuery({
    queryKey: dmsLinkKeys.list(skuId ?? ''),
    enabled: !!skuId,
    queryFn: () =>
      api
        .get<{ dmsLinks: SkuDmsLink[] }>(`/v4/wms/skus/${skuId}/dms-links`)
        .then((r) => r.dmsLinks),
  });
}

interface AddInput {
  skuId: string;
  dmsDocumentId: string;
  kind?: SkuDmsLinkKind;
  label?: string | null;
}

export function useAddSkuDmsLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, dmsDocumentId, kind, label }: AddInput) =>
      api.post<{ dmsLink: SkuDmsLink }>(`/v4/wms/skus/${skuId}/dms-links`, {
        dmsDocumentId,
        kind,
        label,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: dmsLinkKeys.list(vars.skuId) });
      qc.invalidateQueries({ queryKey: wmsSkuKeys.detail(vars.skuId) });
      toast.success('Document linked');
    },
    onError: (e) => {
      toast.error('Failed to link document', { description: e instanceof Error ? e.message : '' });
    },
  });
}

interface PatchInput {
  skuId: string;
  linkId: string;
  patch: Partial<Pick<SkuDmsLink, 'kind' | 'label' | 'sortOrder'>>;
}

export function useUpdateSkuDmsLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, linkId, patch }: PatchInput) =>
      api.patch<{ dmsLink: SkuDmsLink }>(`/v4/wms/skus/${skuId}/dms-links/${linkId}`, patch),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: dmsLinkKeys.list(vars.skuId) });
    },
  });
}

interface DeleteInput {
  skuId: string;
  linkId: string;
}

export function useDeleteSkuDmsLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, linkId }: DeleteInput) =>
      api.delete(`/v4/wms/skus/${skuId}/dms-links/${linkId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: dmsLinkKeys.list(vars.skuId) });
      qc.invalidateQueries({ queryKey: wmsSkuKeys.detail(vars.skuId) });
      toast.success('Document unlinked');
    },
  });
}
